'use client';

import FeaturedPropertyCardGridCarousel from '@components/CardGridWithMobileCarousel/PropertyGrid/PropertyGrid';
import CoListers from '@components/CoListers';
import { propertyTypesToBuy, propertyTypesToRent } from '@components/PropertiesSearch/utils';
import ContactForm from '@components/Forms/ContactForm';
import Block from '@components/Layout/Block';
import DevelopmentDrawer from '@components/PropertyDetailsComponents/DevelopmentDrawer';
import mapDrawerDataDevelopments from '@components/PropertyDetailsComponents/DevelopmentDrawer/mapData';
import DevelopmentFeaturesAmenities from '@components/PropertyDetailsComponents/DevelopmentFeaturesAmenities';
import DevelopmentHeader from '@components/PropertyDetailsComponents/DevelopmentHeader';
import DevelopmentUnitDetails from '@components/PropertyDetailsComponents/DevelopmentUnitDetails';
import Drawer from '@components/PropertyDetailsComponents/Drawer';
import mapDrawerData, { DrawerItem } from '@components/PropertyDetailsComponents/Drawer/mapData';
import ListingHighlights from '@components/PropertyDetailsComponents/ListingHighlights';
import mapListingHighlightsData from '@components/PropertyDetailsComponents/ListingHighlights/mapData';
import Location from '@components/PropertyDetailsComponents/Location';
import MediaGallery from '@components/PropertyDetailsComponents/MediaGallery';
import mapMediaGalleryData, {
  MediaGalleryData,
} from '@components/PropertyDetailsComponents/MediaGallery/mapData';
import PaymentCalculator from '@components/PropertyDetailsComponents/PaymentCalculator';
import PropertyHistory from '@components/PropertyDetailsComponents/PropertyHistory';
import mapPropertyHistoryData from '@components/PropertyDetailsComponents/PropertyHistory/mapData';
import ShareHeader from '@components/PropertyDetailsComponents/ShareHeader';
import ListingSnapshot from '@components/PropertyDetailsComponents/Snapshot';
import TextBlock from '@components/PropertyDetailsComponents/TextBlock';
import { query, space } from '@design/templateFns';
import { Listing, ListingSortKeys, SortDir, StandardStatusEnum } from '@engelvoelkersamericas/api';
import evAPI from '@util/api/ev';
import { PAGE_TYPE } from '@util/constants';
import { useCurrency } from '@util/customHooks/useCurrency';
import getShopUrlPrepend from '@util/getShopUrlPrepend';
import {
  buildDevelopmentUrl,
  buildListUrl,
  getListingCurrency,
  getListingPriceToDisplay,
} from '@util/listings';
import { generateListTracEvent } from '@util/ListTrac';
import { getCorrectHeader, preventWidows } from '@util/locale';
import { getGeoLocationRadius } from '@util/location';
import { getSqFt, mapResults } from '@util/propertiesMapping';
import { getCompiledAddress } from '@util/string';
import { apiClient } from 'api-client';
import { observer } from 'mobx-react-lite';
import { useStores } from 'mobx/useStores';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  drawerLoadingData,
  featuresAmenitiesLoadingData,
  listingHighlightsLoadingData,
  listingSnapshotLoadingData,
  locationLoadingData,
  shareHeaderLoadingData,
} from './loadingStates';
import { ConstellationUtils } from '@engelvoelkersamericas/eva-listings-sdk/lib/client';
import { useMeasurement } from '@util/customHooks/useMeasurement';

interface PropertyDetailPageProps {
  locale: string;
  pageType: 'propertyDetail' | 'developmentDetail';
  propertyData: any;
  demographicData: any;
  listingId: string;
  paymentCalculator: any;
  shopname?: string;
}

const PropertyDetailPage: FC<PropertyDetailPageProps> = observer(
  ({ locale, pageType, propertyData, demographicData, listingId, paymentCalculator, shopname }) => {
    const t = useTranslations();
    const api = new evAPI();
    const { appStore, shopStore } = useStores();
    const { mlsComplianceData } = appStore;
    const { shopChimeId, shopIds, shopPageData } = shopStore;
    const { preferredCurrency, getListingPrice, convertCurrency, formatCurrency } = useCurrency();
    const { preferredMeasurement } = useMeasurement();

    const [shareHeaderData, setShareHeaderData] = useState<{
      [key: string]: any;
    }>(shareHeaderLoadingData);
    const [developmentHeaderDetail, setDevelopmentHeaderDetail] = useState<{
      img?: string;
      header?: string;
      subheaders: string[];
      projectWebsite: string | null;
    } | null>(null);
    const [mediaGalleryData, setMediaGalleryData] = useState<MediaGalleryData | null>();
    const [listingSnapshotData, setListingSnapshotData] = useState<any>(listingSnapshotLoadingData);
    const [textBlockData, setTextBlockData] = useState<{
      header: string;
      bodyText2?: string;
    } | null>(null);
    const [listingHighlightsData, setListingHighlightsData] = useState<any>(
      listingHighlightsLoadingData,
    );
    const [currentListingData, setCurrentListingData] = useState(null);

    const [drawerData, setDrawerData] = useState<DrawerItem[] | null>(drawerLoadingData);
    const [featuresAmenitiesData, setFeaturesAmenitiesData] = useState(
      featuresAmenitiesLoadingData,
    );
    const [locationData, setLocationData] = useState<any>(locationLoadingData);
    const [unitDetailsData, setUnitDetailsData] = useState<{
      activeListings: Listing[];
      pastListings: Listing[];
    } | null>(null);
    const [propertyHistoryData, setPropertyHistoryData] = useState<any>(null);
    const [paymentCalculatorData, setPaymentCalculatorData] = useState(500000);
    const [newestListingsUrl, setNewestListingsUrl] = useState('Newest Listings');
    const [newestListingsCardArr, setNewestListingsCardArr] = useState<Listing[]>([]);
    // const [marketInsightsZipCode, setMarketInsightsZipCode] = useState(null);
    const [showMap, setShowMap] = useState<boolean>(true);
    const [priceSuffix, setPriceSuffix] = useState<string>('');
    const [lowestPrice, setLowestPrice] = useState<number | null>(null); // this will be set with not-yet available data from Developments
    const [coListersArr, setCoListersArr] = useState<any[]>([]);
    const [isFavorite, setIsFavorite] = useState<boolean | null>(null);
    const [shopOsns, setShopOsns] = useState<string[] | null>(null);
    const [chimeObject, setChimeObject] = useState({
      chimeId: '0',
      type: 'EV Global',
    });

    const [currencyCode, setCurrencyCode] = useState<string | undefined>();
    const [baseCurrency, setBaseCurrency] = useState<string | undefined>();
    const [currentPage, setCurrentPage] = useState(1); // Current page for pagination
    const [totalResults, setTotalResults] = useState(0); // Current page for pagination

    //PAGINATION
    // Ref to scrollTo
    const scrollRef = useRef<any | null>(null);

    // Handle selecting a page
    const handlePageSelect = (page: number): null => {
      if (scrollRef.current) {
        const offset = scrollRef.current?.offsetTop - 240;
        window.scrollTo({
          top: offset,
          behavior: 'smooth',
        });
      }
      setCurrentPage(page);
      return null;
    };

    // boolean to check if this has a subdomain or is structured like "/shops/*shopname*/..."
    const shopUrlPrepend = getShopUrlPrepend();

    const {
      BuyerAgentFullName = '',
      BuyerOfficeName = '',
      AttributionContact = '',
    } = propertyData?.resoProperty || {};

    const getPropertyBaseCurrency = (propertyData): string => {
      const country = propertyData?.resoProperty?.Country;
      const originatingSystemName = propertyData?.resoProperty?.OriginatingSystemName;
      const propertyCurrencyCode =
        propertyData?.resoProperty?.evaMetadata?.Currency ||
        propertyData?.resoProperty?.CurrencyCode ||
        shareHeaderData.currencyCode;
      return getListingCurrency({
        originatingSystemName,
        country,
        listingCurrency: propertyCurrencyCode,
        mlsComplianceData,
      });
    };
    //
    //
    // processing property details
    //
    const processPropertyData = async () => {
      const data = propertyData;
      //
      //
      // Initial processing of data
      //
      const baseCurrency = getPropertyBaseCurrency(propertyData);
      const listingData = {
        ...data?.resoProperty,
        Latitude:
          data?.evaMetadata?.Coordinates?.Lat ||
          data?.resoProperty.Latitude ||
          data?.resoProperty.RawMlsLatitude
            ? parseFloat(
                data?.evaMetadata?.Coordinates?.Lat ||
                  data?.resoProperty.Latitude ||
                  data?.resoProperty.RawMlsLatitude,
              )
            : null,
        Longitude:
          data?.evaMetadata?.Coordinates?.Lon ||
          data?.resoProperty.Longitude ||
          data?.resoProperty.RawMlsLongitude
            ? parseFloat(
                data?.evaMetadata?.Coordinates?.Lon ||
                  data?.resoProperty.Longitude ||
                  data?.resoProperty.RawMlsLongitude,
              )
            : null,
        ListUrl:
          pageType === 'developmentDetail'
            ? buildDevelopmentUrl(data?.resoProperty)
            : buildListUrl(data?.resoProperty),
        cooperativeAgencyStatus: data?.resoProperty?.evaMetadata?.CooperativeAgencyYN
          ? t('Cooperative Agency')
          : '',
        cooperativeAgencyName: data?.resoProperty?.evaMetadata?.CooperativeAgencyName || '',
        buyerBrokerOfferToCompensate: data?.resoProperty?.evaMetadata?.BuyerBrokerOfferToCompensate,
        sellerConcessions: data?.resoProperty?.evaMetadata?.SellerConcessions,
        disclaimer: data?.resoProperty?.evaMetadata?.Disclaimer,
        CurrencyCode: baseCurrency,
      };

      setCurrentListingData(listingData);

      //
      //
      // Prepare Data for Sq Footage
      //
      let sqFt = getSqFt(listingData);

      const price = getListingPriceToDisplay({
        standardStatus: listingData.StandardStatus,
        originatingSystemName: listingData.OriginatingSystemName,
        listPrice: listingData.ListPrice,
        closePrice: listingData.ClosePrice,
        closePriceToDisplay: listingData?.evaMetadata?.ClosePriceToDisplay,
        propertyType: listingData.PropertyType,
        leaseAmount: listingData.LeaseAmount,
        leaseAmountFrequency: listingData.LeaseAmountFrequency,
      });

      // Set share header data
      const mappedShareHeaderData = {
        cost: price || 0,
        costLowest: lowestPrice || 0,
        closePrice: listingData?.ClosePrice,
        closePriceToDisplay: listingData?.evaMetadata?.ClosePriceToDisplay,
        address: getCompiledAddress(listingData),
        currencyCode: listingData?.evaMetadata?.Currency || listingData?.CurrencyCode,
        url: listingData.ListUrl,
        favId: listingData.favId,
        setIsFavorite: setIsFavorite,
        originatingSystemName:
          listingData?.evaMetadata?.OriginatingSystemName || listingData.OriginatingSystemName,
        listAgentFullName:
          listingData?.evaMetadata?.ListAgentFullName || listingData.ListAgentFullName,
        listAgentMlsId: listingData?.evaMetadata?.ListAgentMlsId || listingData.ListAgentMlsId,
        listOfficeName: listingData?.evaMetadata?.ListOfficeName || listingData.ListOfficeName,
        listOfficeMlsId: listingData?.evaMetadata?.ListOfficeMlsId || listingData.ListOfficeMlsId,
        listingId: listingData?.evaMetadata?.ListingId || listingData.ListingId,
        startingFrom: listingData?.evaMetadata?.DevelopmentData?.StartingFrom,
        zipCode: listingData.PostalCode || null,
        country: listingData.Country,
        pageType: pageType,
        isCooperativeAgency: Boolean(listingData.cooperativeAgencyStatus),
      };
      setShareHeaderData(mappedShareHeaderData);
      setIsFavorite(listingData.isFavorite || false);
      //
      //
      // Development Header Data
      //
      if (pageType === 'developmentDetail') {
        const imgUrl = listingData?.evaMetadata?.Media?.find(
          (media) => media.MediaCategory === 'Office Logo',
        )?.MediaURL;

        const subheader: string[] = [];

        if (listingData?.PropertySubType) {
          const numOfResidences = listingData?.evaMetadata?.DevelopmentData?.NumberOfResidences;

          if (numOfResidences) {
            subheader.push(`${numOfResidences + ' ' + listingData?.PropertySubType}s`);
          } else {
            subheader.push(listingData?.PropertySubType);
          }
        }

        if (listingData?.PropertyType) {
          subheader.push(listingData?.PropertyType);
        }

        const developmentHeaderDetail = {
          img: imgUrl || null,
          header: getCorrectHeader(listingData?.evaMetadata?.ExposeTitle, locale),
          subheaders: subheader,
          projectWebsite: listingData?.evaMetadata?.DevelopmentData?.DevelopmentWebsite,
        };
        setDevelopmentHeaderDetail(developmentHeaderDetail);
      }

      //
      //
      // Media Gallery Data
      //
      setMediaGalleryData(mapMediaGalleryData(listingData));

      //
      //
      // Listing Snapshot Data
      //
      if (pageType === 'developmentDetail') {
        const listingSnapshotData = {
          listUrl: listingData.ListUrl,
          propertyType: null,
          bathrooms: null,
          bedrooms: null,
          squareFeet: sqFt,
          acreage: null,
          listOfficeName: null,
          listingId: null,
          listAgentFullName: null,
          includeBrokeragePhone: null,
          brokeragePhone: null,
          showLicenseNumber: false,
          member: null,
          country: null,
        };
        setListingSnapshotData(listingSnapshotData);
      } else {
        const listingSnapshotData = {
          listUrl: listingData.ListUrl,
          evaId: listingData.evaMetadata.EvaId,
          propertyType: listingData.PropertySubType,
          bathrooms: listingData.BathroomsTotalInteger || listingData.RawMlsBathroomsTotal,
          bedrooms: listingData.BedroomsTotal,
          squareFeet: sqFt,
          acreage: listingData.LotSizeAcres,
          listOfficeName: listingData?.evaMetadata?.ListOfficeName || listingData.ListOfficeName,
          listingId: listingData?.evaMetadata?.ListingId || listingData.ListingId,
          listAgentFullName:
            listingData?.evaMetadata?.ListAgentFullName || listingData.ListAgentFullName,
          includeBrokeragePhone: listingData.includeBrokeragePhone,
          brokeragePhone: listingData.ListOfficePhone,
          member: listingData.Member,
          country: listingData.Country,
          originatingSystemName: listingData.OriginatingSystemName,
          isCooperativeAgency: Boolean(listingData.cooperativeAgencyStatus),
          cooperativeAgencyName: listingData.cooperativeAgencyName,
        };
        if (listingData.OriginatingSystemName == 'NorthwestMLS' && listingData.Custom15 == 'false')
          setShowMap(false);
        if (
          listingData.OriginatingSystemName == 'CENTRIS' &&
          (listingData.Custom14 || listingData.Custom15)
        )
          setPriceSuffix(' +taxes');
        setListingSnapshotData(listingSnapshotData);
      }

      //
      //
      // Property Detail Block Data
      //
      // Header
      let header;
      if (listingData?.evaMetadata?.ExposeTitle) {
        header = getCorrectHeader(listingData?.evaMetadata?.ExposeTitle, locale);
      } else if (listingData?.ExposeTitle) {
        header = getCorrectHeader(listingData?.ExposeTitle, locale);
      }
      const updatedListingData = {
        ...listingData,
        Enhancement: [listingData?.evaMetadata],
      };
      const enhancementPublicRemarks = ConstellationUtils.getPublicRemarks(
        updatedListingData,
        locale as any,
      );

      const frenchCentrisPublicRemarks = listingData.Custom5?.PublicRemarks;

      const evaMetadataPublicRemarks = getCorrectHeader(
        listingData?.evaMetadata?.PublicRemarks,
        locale,
      );

      // Body Text
      let bodyText2;
      if (
        listingData?.OriginatingSystemName == 'CENTRIS' &&
        locale.toLowerCase() === 'fr-ca' &&
        frenchCentrisPublicRemarks
      ) {
        bodyText2 = frenchCentrisPublicRemarks;
      } else if (!!enhancementPublicRemarks) {
        bodyText2 = enhancementPublicRemarks;
      } else if (evaMetadataPublicRemarks) {
        bodyText2 = evaMetadataPublicRemarks;
      } else if (listingData?.PublicRemarks) {
        bodyText2 = getCorrectHeader(listingData?.PublicRemarks, locale);
      }
      setTextBlockData({
        header: header ? preventWidows(header) : null,
        bodyText2,
      });

      //
      //
      // Drawer Block Data
      //
      if (pageType === 'developmentDetail') {
        setDrawerData(mapDrawerDataDevelopments(listingData, t));
      } else {
        setDrawerData(mapDrawerData(listingData, locale, t));
      }

      //
      //
      // Features and Amenities Block Data
      //
      const featuresAmenitiesList =
        listingData?.evaMetadata?.BuildingFeatures || listingData?.BuildingFeatures || null;
      if (
        pageType === 'developmentDetail' &&
        featuresAmenitiesData &&
        featuresAmenitiesData?.length > 0
      ) {
        setFeaturesAmenitiesData(featuresAmenitiesList);
      }

      //
      //
      // Location Block Data
      //
      setLocationData({
        state: listingData.StateOrProvince,
        city: listingData.City,
        zip: listingData.PostalCode,
        neighborhood: listingData.CountyOrParish,
        address: listingData.UnparsedAddress,
        center: {
          lat: listingData.Latitude || null,
          lng: listingData.Longitude || null,
        },
        mappedResults: mapResults([{ ...listingData }]),
      });

      // Set Zip code for market details only if the country is US
      // const isUS = listingData.Country === 'US';
      // const zipCode = listingData.PostalCode;
      // if (isUS && zipCode) {
      //   setMarketInsightsZipCode(zipCode);
      // }

      //
      //
      // Property History Data
      //
      if (pageType === 'propertyDetail') {
        setPropertyHistoryData(mapPropertyHistoryData(listingData));
      }

      //
      //
      // Payment Calculator Block Data
      //
      setPaymentCalculatorData(listingData.ListPrice ? listingData.ListPrice : 500000);

      setNewestListingsUrl(getNearListingsUrl());

      const apiAgentArray: any[] = [];
      const coListerData: any[] = [];
      // Get co-lister data
      const pushToApiAgentArray = (agentMlsId) => {
        if (agentMlsId) {
          apiAgentArray.push({
            osn: listingData.evaMetadata.OriginatingSystemName || listingData.OriginatingSystemName,
            agentMlsId,
          });
        }
      };
      pushToApiAgentArray(listingData?.evaMetadata?.ListAgentMlsId || listingData.ListAgentMlsId);
      pushToApiAgentArray(
        listingData?.evaMetadata?.CoListAgentMlsId || listingData.CoListAgentMlsId,
      );
      listingData?.Custom6 && pushToApiAgentArray(listingData.Custom6);
      listingData?.Custom2 &&
        listingData?.OriginatingSystemName === 'VancouverBC' &&
        pushToApiAgentArray(listingData.Custom2);

      const firstCustomAgent = listingData?.Custom10;
      const secondCustomAgent = listingData?.Custom11;

      firstCustomAgent && pushToApiAgentArray(firstCustomAgent);
      secondCustomAgent && pushToApiAgentArray(secondCustomAgent);
      // check apiAgentArray and remove any duplicates
      const uniqueAgentArray = apiAgentArray.filter(
        (v, i, a) => a.findIndex((t) => t.agentMlsId === v.agentMlsId) === i,
      );

      let localShopOsns;
      let advisorDataQuery: any = { records: [] };

      if (uniqueAgentArray.length > 0) {
        const advisorOSNandMLSIdQuery = await api.advisorDetailsOSNandMLSId(uniqueAgentArray);

        if (advisorOSNandMLSIdQuery && advisorOSNandMLSIdQuery.records.length > 0) {
          advisorDataQuery = advisorOSNandMLSIdQuery;
        } else if (pageType === 'developmentDetail') {
          const bioQuery = await api.advisorBio(
            listingData?.evaMetadata?.ListAgentMlsId || listingData.ListAgentMlsId,
          );

          advisorDataQuery = {
            records: [bioQuery],
          };
        }

        if (advisorDataQuery?.records.length > 0) {
          const mlsIds: string[] = [];
          mlsIds.push(listingData.CoListAgentMlsId);
          mlsIds.push(listingData.ListAgentMlsId);
          mlsIds.push(listingData.ListOfficeMlsId);
          mlsIds.push(listingData.CoListOfficeMlsId);
          for (const advisorData of advisorDataQuery.records) {
            // if advisor has the same phone as another than skip it
            // Logic to ensure the proper account tied to the listing is being used based off of the listing's MLS data
            if (advisorData) {
              if (advisorData?.accounts?.length) {
                let account = advisorData.accounts[0];
                for (let i = 0; i < advisorData.accounts.length; i++) {
                  const accountMlsIds = advisorData.accounts[i].mls.map((m) => m.mlsId);
                  for (let j = 0; j < accountMlsIds.length; j++) {
                    if (mlsIds.includes(accountMlsIds[j])) {
                      account = advisorData.accounts[i];
                      break;
                    }
                  }
                }
                const phone = account.phoneNumbers[0]?.phone;
                const license = account.phoneNumbers[0]?.license;
                const formattedCoListerData = {
                  img: advisorData?.profileThumbnail ? advisorData?.profileThumbnail : null,
                  name:
                    advisorData?.name?.firstName || advisorData?.name?.lastName
                      ? `${advisorData?.name?.firstName} ${advisorData?.name?.lastName}`
                      : null,
                  memberUserId: account.chimeInfo?.memberUserId || '0',
                  phone: phone ? phone : null,
                  license: license ? license : null,
                };
                const isPrimaryListingAgent =
                  advisorData?.accounts[0]?.mls?.filter(
                    (mls) => mls.mlsId === listingData.ListAgentMlsId,
                  ).length > 0;
                if (isPrimaryListingAgent) {
                  coListerData.unshift(formattedCoListerData);
                } else {
                  coListerData.push(formattedCoListerData);
                }
              }

              if (advisorData.type === 'team') {
                const formattedCoListerData = {
                  img: advisorData.teamPicture,
                  name: advisorData.name,
                  memberUserId: advisorData.chimeInfo?.chimeId || '0',
                  phone: advisorData.contact?.phone,
                };

                coListerData.push(formattedCoListerData);
              }
            }
          }

          localShopOsns = getShopOsns(advisorDataQuery.records);
        }

        if (coListerData.length > 0) {
          setCoListersArr(coListerData);
        }
      }
      setShopOsns(localShopOsns ?? []);
    };

    const associatedShopOsns = (associatedShops) => {
      const shop = associatedShops?.find((associatedShop) => {
        if (associatedShop?.mls?.length > 0) {
          const shopOsn = propertyData?.resoProperty?.OriginatingSystemName;
          const shopMlsId = propertyData?.resoProperty?.ListOfficeMlsId;
          const shopMls = associatedShop.mls.find(
            (mls) => mls?.mls?.osn == shopOsn && mls?.mlsId == shopMlsId,
          );
          if (shopMls != null) {
            return true;
          }
        }
      });
      if (shop != null) {
        const osnSet = new Set();
        const osns = shop.mls?.map((mls) => mls?.mls?.osn);
        for (let osn of osns) {
          osnSet.add(osn);
        }
        return Array.from(osnSet);
      }
    };

    function getShopOsns(advisorRecords) {
      if (advisorRecords && advisorRecords.length > 0) {
        for (const advisorData of advisorRecords) {
          if (advisorData?.accounts?.length) {
            for (const account of advisorData?.accounts) {
              return associatedShopOsns(account?.associatedShops);
            }
          }

          if (advisorData.type === 'team') {
            return associatedShopOsns(advisorData?.associatedShops);
          }
        }
      }
      return null;
    }

    useEffect(() => {
      setShareHeaderData(shareHeaderLoadingData);
      setListingSnapshotData(listingSnapshotLoadingData);
      setTextBlockData(null);
      setListingHighlightsData(listingHighlightsLoadingData);
      setDrawerData(drawerLoadingData);
      setFeaturesAmenitiesData(featuresAmenitiesLoadingData);
      setLocationData(locationLoadingData);
      setPaymentCalculatorData(500000);
      setNewestListingsCardArr([]);

      if (propertyData) {
        processPropertyData();
      }
    }, [propertyData]);

    useEffect(() => {
      //
      // Listing Highlights Block Data
      //
      if (currentListingData) {
        if (pageType === 'propertyDetail') {
          setListingHighlightsData(
            mapListingHighlightsData({
              listingData: currentListingData,
              locale: locale,
              demographicsData: demographicData,
              preferredMeasurement: preferredMeasurement || '',
              t,
            }),
          );
        }

        generateListTracEvent({
          type: 'view',
          listingId: propertyData.resoProperty.ListingId,
          zipCode: propertyData.resoProperty.PostalCode,
        });
      }
    }, [
      demographicData,
      pageType,
      currentListingData,
      locale,
      preferredCurrency,
      preferredMeasurement,
    ]);

    //
    //
    // Development Unit Data
    //
    // function for querying the development unit data
    const getDevelopmentUnitData = async (developmentId: string) => {
      try {
        const res = await apiClient.getListingsByDevelopment({
          id: developmentId,
          page: currentPage,
          size: 10,
          sortKey: ListingSortKeys.UsdListPrice,
          sortDir: SortDir.Desc,
        });

        const listings = res.data.listings;
        const activeListings = listings.filter(
          (property) => property.StandardStatus === StandardStatusEnum.Active,
        );
        const pastListings = listings.filter(
          (property) => property.StandardStatus !== StandardStatusEnum.Active,
        );
        const lowestPrice = Math.min(...activeListings.map((property) => property.ListPrice ?? 0));
        setUnitDetailsData({
          activeListings,
          pastListings,
        });
        setTotalResults(res.data.totalRecords);
        setLowestPrice(lowestPrice);
      } catch (error) {
        console.error(error);
      }
    };

    useEffect(() => {
      const developmentId = propertyData?.resoProperty?.evaMetadata?.DevelopmentId;
      if (pageType === 'developmentDetail' && developmentId) {
        getDevelopmentUnitData(developmentId);
      }
    }, [propertyData?.resoProperty?.evaMetadata?.DevelopmentId, currentPage]);

    const getNearByListingsParams = () => {
      let { ListPrice } = propertyData.resoProperty,
        maxPrice = ListPrice + Math.floor(ListPrice * 0.2),
        minPrice = ListPrice - Math.floor(ListPrice * 0.2);

      /** Getting latitude from evaMetadata */
      let Latitude =
        propertyData?.resoProperty?.evaMetadata?.Coordinates?.Lat ??
        propertyData?.resoProperty?.evaMetadata?.Latitude;
      let Longitude =
        propertyData?.resoProperty?.evaMetadata?.Coordinates?.Lon ??
        propertyData?.resoProperty?.evaMetadata?.Longitude;

      /** Get from listing */
      Latitude =
        Latitude ??
        propertyData?.resoProperty?.Latitude ??
        propertyData?.resoProperty?.RawMlsLatitude;
      Longitude =
        Longitude ??
        propertyData?.resoProperty?.Longitude ??
        propertyData?.resoProperty?.RawMlsLongitude;

      const propertyType = propertyData?.resoProperty?.PropertyType || 'Residential';
      return {
        minPrice,
        maxPrice,
        Latitude,
        Longitude,
        radius: 20,
        propertyTypes: [propertyType],
        listingStatus: ['Active'],
        isBuy: propertyTypesToBuy.includes(propertyType) ? true : false,
      };
    };

    const getNearListingsUrl = () => {
      const {
        minPrice,
        maxPrice,
        Latitude,
        Longitude,
        radius,
        propertyTypes,
        listingStatus,
        isBuy,
      } = getNearByListingsParams();

      const queryParams = new URLSearchParams({
        minPrice,
        maxPrice,
        Latitude,
        Longitude,
        radius,
        propertyTypes,
        listingStatus,
        pageType: PAGE_TYPE.SIMILAR_LISTINGS,
        osns: shopOsns,
        listingTypes: isBuy ? ['For Sale'] : ['For Rent'],
      } as any);

      return `${shopUrlPrepend ? shopUrlPrepend : ''}/properties?${queryParams}`;
    };

    //
    //
    // Newest Listings Data
    //
    const fetchAndSetCardArr = async () => {
      const { minPrice, maxPrice, Latitude, Longitude, radius, isBuy } = getNearByListingsParams();
      try {
        if (Latitude == null || Longitude == null) {
          throw new Error('Latitude or Longitude is not present for the listing');
        }
        const polygonData = getGeoLocationRadius(Latitude, Longitude, radius);

        const data = await apiClient.getListings({
          page: 1,
          size: 6,
          sortKey: ListingSortKeys.OnMarketDate,
          sortDir: SortDir.Desc,
          listingFilters: {
            geoPolygons: polygonData,
            maxListPrice: maxPrice,
            minListPrice: minPrice,
            propertyTypes: isBuy ? propertyTypesToBuy : propertyTypesToRent,
            listingStatuses: [StandardStatusEnum.Active],
            shopIds: shopIds ? shopIds : undefined,
          },
        });

        //check if any results match current listing's id, if so remove it, if not, remove the last one
        const filteredResults = data?.data?.listings?.filter((property: any) => {
          return property.ListingId !== listingId;
        });
        if (filteredResults.length === 7) {
          filteredResults.pop();
        }

        setNewestListingsCardArr(filteredResults);
        setNewestListingsUrl(getNearListingsUrl());
      } catch (error) {
        console.error(error);
      }
    };

    useEffect(() => {
      if (pageType === 'propertyDetail' && shopOsns != null) {
        fetchAndSetCardArr();
      }
    }, [pageType, shopOsns]);

    useEffect(() => {
      if (pageType === 'developmentDetail' && coListersArr.length > 0) {
        setChimeObject({ chimeId: coListersArr[0]?.memberUserId, type: 'Agent' });
      }
    }, [pageType, coListersArr]);

    const [price, setPrice] = useState<string>('$0');
    const [startingFromPrice, setStartingFromPrice] = useState<string>('');

    useEffect(() => {
      const listing = propertyData.resoProperty;
      const currency = listing.evaMetadata?.Currency || listing.CurrencyCode;
      const { formattedPrice, originalCurrency, displayedCurrency } = getListingPrice({
        standardStatus: listing.StandardStatus,
        originatingSystemName: listing.OriginatingSystemName,
        listPrice: listing.ListPrice!,
        closePrice: listing.ClosePrice,
        closePriceToDisplay: listing.ClosePriceToDisplay,
        country: listing.Country,
        listingCurrency: currency,
        propertyType: listing.PropertyType,
        leaseAmount: listing.LeaseAmount,
        leaseAmountFrequency: listing.LeaseAmountFrequency,
        custom14: listing.custom14,
        custom15: listing.custom15,
      });
      if (propertyData?.resoProperty?.evaMetadata?.PriceUponRequest) {
        setPrice(t('Price Upon Request'));
      } else {
        setPrice(formattedPrice);
      }

      if (shareHeaderData?.startingFrom) {
        const startingFrom = convertCurrency(shareHeaderData.startingFrom, originalCurrency);
        setStartingFromPrice(
          `${displayedCurrency} ${formatCurrency(startingFrom, displayedCurrency)}`,
        );
      }

      setBaseCurrency(originalCurrency);
      setCurrencyCode(displayedCurrency);
    }, [propertyData, getListingPrice]);

    const nerenText = (
      <>
        {AttributionContact && `${AttributionContact}`};{' '}
        {BuyerAgentFullName && `Sold by ${BuyerAgentFullName}`}
        {BuyerOfficeName && `, ${BuyerOfficeName}`}
      </>
    );

    const isBuy = propertyTypesToBuy.includes(
      propertyData?.resoProperty?.PropertyType || 'Residential',
    );

    const mlsCompliance = mlsComplianceData?.[propertyData?.resoProperty?.OriginatingSystemName];
    const propertyCurrency = getPropertyBaseCurrency(propertyData);

    return (
      <>
        <StyledBlock squish>
          <ShareHeader
            {...{
              ...shareHeaderData,
              cost: price ? `${price}${priceSuffix}` : undefined,
              startingFrom: startingFromPrice,
              isFavorite: isFavorite,
              projectWebsite: developmentHeaderDetail?.projectWebsite ?? undefined,
              listingId: shareHeaderData.listingId!,
              listOfficeMlsId: shareHeaderData.listOfficeMlsId!,
              zipCode: shareHeaderData.zipCode!,
            }}
          />
          {pageType === 'developmentDetail' && developmentHeaderDetail && (
            <DevelopmentHeader {...developmentHeaderDetail} />
          )}
          {mediaGalleryData && <MediaGallery {...mediaGalleryData} />}
          <ListingSnapshot
            mlsComplianceObj={mlsCompliance ?? {}}
            {...listingSnapshotData}
            nerenComplianceNode={nerenText}
          />
        </StyledBlock>
        {(textBlockData?.header || textBlockData?.bodyText2) && (
          <TextBlock
            header={textBlockData.header}
            body={textBlockData.bodyText2}
            swatch={'cream'}
          />
        )}
        {!propertyData.resoProperty?.evaMetadata?.CooperativeAgencyYN &&
          coListersArr.length > 0 && <CoListers data={coListersArr}></CoListers>}
        {pageType === 'propertyDetail' && (
          <ListingHighlights tabs={listingHighlightsData} currencyCode={propertyCurrency} />
        )}
        {drawerData && (
          <>
            {pageType === 'developmentDetail' ? (
              <DevelopmentDrawer drawers={drawerData} />
            ) : (
              <Drawer
                drawers={drawerData}
                baseCurrency={baseCurrency}
                currencyCode={currencyCode}
              />
            )}
          </>
        )}
        {featuresAmenitiesData?.length > 0 && pageType === 'developmentDetail' && (
          <DevelopmentFeaturesAmenities list={featuresAmenitiesData} />
        )}
        {pageType === 'developmentDetail' && unitDetailsData && (
          <DevelopmentUnitDetails
            eventHistory={unitDetailsData}
            custom14={propertyData?.resoProperty?.Custom14}
            custom15={propertyData?.resoProperty?.Custom15}
            currentPage={currentPage}
            handlePageSelect={handlePageSelect}
            totalResults={totalResults}
            shopname={shopname}
          />
        )}

        {showMap && <Location {...locationData} showDrawAreaButton={false} />}

        {pageType === 'propertyDetail' && propertyHistoryData && (
          <PropertyHistory eventHistory={propertyHistoryData} locale={locale} />
        )}

        {!shopPageData?.fields.hideMortgageCalculator &&
          pageType === 'propertyDetail' &&
          isBuy &&
          currencyCode && (
            <PaymentCalculator
              currencyCode={currencyCode}
              mlsComplianceObj={mlsCompliance}
              defaultValue={Number(price?.replace(/[^\d.]/g, '') || 0)}
              disclaimer={paymentCalculator?.items[0]?.fields?.bodyText}
              modificationTimeStamp={propertyData?.resoProperty?.ModificationTimestamp}
            />
          )}
        {pageType === 'propertyDetail' && newestListingsCardArr.length > 0 ? (
          <FeaturedPropertyCardGridCarousel
            header={t('Similar Homes You May Like')}
            swatch="cream"
            listings={newestListingsCardArr}
            ctaUrl={newestListingsUrl}
            ctaText={t('View More Listings')}
            searchLink
            customBtns={[]}
          />
        ) : (
          <></>
        )}
        {pageType === 'developmentDetail' && (
          <ContactForm
            header={t('ContactForm.title')}
            bodyText={t("Share a few details, and we'll be in touch")}
            ctaText={t('Send Message')}
            disclaimer={t('ContactForm.disclaimer')}
            chimeId={chimeObject?.chimeId}
            chimeType={chimeObject?.type}
            ownershipId={shopChimeId?.toString()}
          />
        )}
      </>
    );
  },
);

export default PropertyDetailPage;

const StyledBlock = styled(Block)`
  padding-top: ${space('lg')};

  @media screen and (${query.atLeast('desktop')}) {
    padding-top: ${space('x5')};
  }
`;
